import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef } from "react";

function normalizeWsBase(raw) {
  const fallback = "ws://localhost:1234";
  if (!raw) return fallback;
  let s = String(raw).trim();
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1);
  }
  if (/^https?:\/\//i.test(s)) return s.replace(/^http/i, "ws");
  if (/^wss?:\/\//i.test(s)) return s;
  return `wss://${s}`;
}

/**
 * Whole-array sync for `textAnnotations`
 * onRemote(arr): gets the whole array from room
 * syncTextAnnotations(arr): replaces Y.Array contents
 */
export default function useCollabTextAnnotations(projectId, onRemote) {
  const ydocRef = useRef(null);
  const yArrRef = useRef(null);

  const cbRef = useRef(onRemote);
  useEffect(() => {
    cbRef.current = onRemote;
  }, [onRemote]);

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();
    const base = normalizeWsBase(process.env.REACT_APP_SOCKET_URL);
    const room = String(projectId).trim();

    const provider = new WebsocketProvider(base, room, ydoc);
    const yArr = ydoc.getArray("textAnnotations");

    const observer = () => {
      const arr = yArr.toJSON();
      cbRef.current?.(Array.isArray(arr) ? arr : []);
    };

    yArr.observe(observer);
    observer(); // initial hydrate

    ydocRef.current = ydoc;
    yArrRef.current = yArr;

    return () => {
      try {
        yArr.unobserve(observer);
      } catch {}
      try {
        provider.destroy();
      } catch {}
      try {
        ydoc.destroy();
      } catch {}
      ydocRef.current = null;
      yArrRef.current = null;
    };
  }, [projectId]);

  const syncTextAnnotations = (arr) => {
    const yArr = yArrRef.current;
    const ydoc = ydocRef.current;
    if (!yArr || !ydoc) return;

    const next = Array.isArray(arr) ? arr : [];
    ydoc.transact(() => {
      yArr.delete(0, yArr.length);
      if (next.length) yArr.insert(0, next);
    });
  };

  return { syncTextAnnotations };
}
